import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  FormControl,
  FormLabel,
  SimpleGrid,
  Stack,
  Switch,
} from '@chakra-ui/react';
import { useFetchAfesPageQuery, useFetchVendorsPageQuery } from '../../api';
import { useAuth } from '../../core';
import {
  AfeAutoComplete,
  AppInput,
  FieldError,
  FormButtonGroup,
  VendorAutoComplete,
} from '../components';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { PutInvoiceRequest } from '../../api/bindings/PutInvoiceRequest';
import { DatePicker } from 'features';
import { useEffect } from 'react';

export interface InvoiceUpdateFormDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  invoice: PutInvoiceRequest;
  onSubmit: (form: PutInvoiceRequest & { includeRevision: boolean }) => void;
  onCancel: () => void;
}

interface Form extends PutInvoiceRequest {
  includeRevision: boolean;
}

export const InvoiceUpdateFormDrawerContent = (
  props: InvoiceUpdateFormDrawerContentProps
) => {
  const { invoice, onSubmit, onCancel, ...rest } = props;

  // Fetches
  const auth = useAuth();
  const { data: afes } = useFetchAfesPageQuery(
    { all: true, clientId: auth?.user.clientId ?? '0' },
    { skip: !auth?.user.clientId }
  );
  const { data: vendors } = useFetchVendorsPageQuery(
    {
      clientId: auth?.user.clientId || '0',
      all: true,
    },
    { skip: !auth?.user.clientId }
  );

  // State
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      // id: invoice.id,
      vendorId: invoice.vendorId,
      name: invoice.name,
      description: invoice.description,
      reference: invoice.reference,
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      receiveDate: invoice.receiveDate,
      serviceThroughDate: invoice.serviceThroughDate,
      paidDate: invoice.paidDate,
      includeRevision: false,
    },
  });
  const { control, register, formState, handleSubmit, reset, watch } = form;

  const invoiceName = watch('name');

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>Update Invoice: {invoiceName}</DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              {/* <FormControl>
                <FormLabel htmlFor="afe">AFE:</FormLabel>
                <Controller
                  name="afeId"
                  control={control}
                  render={(p) => (
                    <AfeAutoComplete
                      id="afe"
                      items={afes?.items}
                      {...p.field}
                      isDisabled={true}
                      value={invoice.afeId}
                    />
                  )}
                />
              </FormControl> */}
              <FormControl>
                <FormLabel htmlFor="name">Name:</FormLabel>
                <AppInput
                  id="name"
                  {...register('name', {
                    minLength: {
                      value: 3,
                      message: 'Name must be between 3 and 256 characters.',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Name must be between 3 and 256 characters.',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="description">Description:</FormLabel>
                <AppInput
                  id="description"
                  {...register('description', {
                    minLength: {
                      value: 3,
                      message:
                        'Description must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message:
                        'Description must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="reference">Reference:</FormLabel>
                <AppInput
                  id="reference"
                  {...register('reference', {
                    minLength: {
                      value: 3,
                      message:
                        'Reference must be between 3 and 256 characters in length',
                    },
                    maxLength: {
                      value: 256,
                      message:
                        'Reference must be between 3 and 256 characters in length',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="vendor">Vendor:</FormLabel>
                <Controller
                  name="vendorId"
                  control={control}
                  render={(f) => (
                    <VendorAutoComplete
                      items={vendors?.items}
                      {...f.field}
                      isDisabled={true}
                      value={invoice.vendorId}
                    />
                  )}
                />
                <FieldError name="vendorId" />
              </FormControl>
              <SimpleGrid columns={[1, 3]} gap={[1, 4]}>
                <FormControl>
                  <FormLabel htmlFor="invoiceDate">Invoice Date:</FormLabel>
                  <DatePicker name="invoiceDate" control={control} />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="receiveDate">Receive Date:</FormLabel>
                  <DatePicker name="receiveDate" control={control} />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="dueDate">Due Date:</FormLabel>
                  <DatePicker name="dueDate" control={control} />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="serviceThroughDate">
                    Service Through Date:
                  </FormLabel>
                  <DatePicker name="serviceThroughDate" control={control} />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="paidDate">Paid:</FormLabel>
                  <DatePicker name="paidDate" control={control} />
                </FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel>Include a Revision?</FormLabel>
                <Controller
                  control={control}
                  name="includeRevision"
                  render={(p) => (
                    <Switch
                      size="lg"
                      isChecked={p.field.value}
                      isDisabled={false}
                      // GH-35
                      onChange={() => {
                        p.field.onChange(!p.field.value);
                      }}
                    />
                  )}
                />
              </FormControl>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onConfirm={handleSubmit(onSubmit)}
              onCancel={() => {
                reset();
                onCancel();
              }}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
